import type { Subscription, BillingCycle } from '../types/subscription';

const STORAGE_KEY = 'debtracker_subscriptions';

export const generateId = (): string =>
  Math.random().toString(36).substring(2, 10) + Date.now().toString(36);

// ─── CRUD ────────────────────────────────────────────────────────────────────

export const getAllSubscriptions = (): Subscription[] => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Subscription[]) : [];
  } catch {
    return [];
  }
};

const saveAll = (subs: Subscription[]) =>
  localStorage.setItem(STORAGE_KEY, JSON.stringify(subs));

export const createSubscription = (
  sub: Omit<Subscription, 'id' | 'createdAt' | 'updatedAt'>
): Subscription => {
  const now = new Date().toISOString();
  const newSub: Subscription = { ...sub, id: generateId(), createdAt: now, updatedAt: now };
  saveAll([...getAllSubscriptions(), newSub]);
  return newSub;
};

export const updateSubscription = (
  id: string,
  updates: Partial<Omit<Subscription, 'id' | 'createdAt'>>
): Subscription | null => {
  const all = getAllSubscriptions();
  const idx = all.findIndex((s) => s.id === id);
  if (idx === -1) return null;
  all[idx] = { ...all[idx], ...updates, updatedAt: new Date().toISOString() };
  saveAll(all);
  return all[idx];
};

export const deleteSubscription = (id: string): boolean => {
  const all = getAllSubscriptions();
  const filtered = all.filter((s) => s.id !== id);
  if (filtered.length === all.length) return false;
  saveAll(filtered);
  return true;
};

// ─── STATS ───────────────────────────────────────────────────────────────────

/** Convert any billing cycle to a monthly equivalent amount */
export const toMonthlyAmount = (amount: number, cycle: BillingCycle): number => {
  switch (cycle) {
    case 'weekly':     return amount * 4.33;
    case 'monthly':    return amount;
    case 'quarterly':  return amount / 3;
    case 'yearly':     return amount / 12;
  }
};

export interface SubscriptionStats {
  activeCount: number;
  trialCount: number;
  monthlyTotal: number;
  yearlyTotal: number;
  categoryBreakdown: Record<string, number>; // category -> monthly spend
}

export const getSubscriptionStats = (): SubscriptionStats => {
  const active = getAllSubscriptions().filter(
    (s) => s.status === 'active' || s.status === 'trial'
  );

  let monthlyTotal = 0;
  const categoryBreakdown: Record<string, number> = {};

  for (const sub of active) {
    const monthly = toMonthlyAmount(sub.amount, sub.billingCycle);
    monthlyTotal += monthly;
    categoryBreakdown[sub.category] = (categoryBreakdown[sub.category] || 0) + monthly;
  }

  return {
    activeCount: active.filter((s) => s.status === 'active').length,
    trialCount: active.filter((s) => s.status === 'trial').length,
    monthlyTotal,
    yearlyTotal: monthlyTotal * 12,
    categoryBreakdown,
  };
};
